<template>
  <div class="steps-controller">
    <div
      class="steps-controller__button steps-controller__left-buttons"
    >
      <step-button
        v-for="(step, order) in steps"
        :key="`step_${step.name}_${order}`"
        :show="step.view"
        :name="step.name"
        :disabled="disabled"
      >
        <span>{{ step.title }}</span>
      </step-button>
    </div>
    <div
      class="steps-controller__button steps-controller__right-buttons"
      :class="[
        { 'steps-controller__right-buttons_4': hasVideo },
      ]"
    >
      <video-button v-if="hasVideo"
        style="order: 1.0"
      ></video-button>
      <back-button
        style="order: 1.1;"
      >
        <span>{{ backButtonTitle }}</span>
      </back-button>
      <template v-if="toStepButtonsList?.length">
        <to-step-button
          v-for="step in toStepButtonsList"
          :to="step.stepName"
          :name-aria="step.name"
          style="order: 1.2;"
          :disabled="disabled"
        >
          <span>{{ step.title }}</span>
        </to-step-button>
      </template>
      <next-button
        style="order: 1.3;"
        :disabled="(isLastStep && useCustomNext === false) || disabled"
        :useCustomEvent="useCustomNext"
        @customEvent="handleCustomNext"
      >
        <span>{{ nextButtonTitle }}</span>
      </next-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import pinia from '../../core/pinia';
import { useConfigStore, useStepsStore } from '../../stores';

import StepButton from './StepButton.vue';
import NextButton from './NextButton.vue';
import BackButton from './BackButton.vue';
import ToStepButton from './ToStepButton.vue';
import VideoButton from './VideoButton.vue';

import type { ILineItem } from '../../types/line-item.types';
import { E_STEPS_CONTROLLER_EVENT } from '../../types/components-events.enums';
import type { TStepsControllerEventResponse } from '../../types/event-response.types';

interface IProps {
  useCustomNext?: boolean;
  disabled?: boolean;
}
defineProps<IProps>();
const emit = defineEmits<{
  (e: E_STEPS_CONTROLLER_EVENT.CUSTOM_NEXT, payload: TStepsControllerEventResponse[E_STEPS_CONTROLLER_EVENT.CUSTOM_NEXT]): TStepsControllerEventResponse;
}>();

const configStore = useConfigStore(pinia);
const stepsStore = useStepsStore(pinia);

const buttonTitles = computed(() => configStore.getButtonTitles);
const backButtonTitle = computed(() => buttonTitles.value.back);
const nextButtonTitle = computed(() => buttonTitles.value.next);
const steps = computed(() => stepsStore.getSteps);
const isLastStep = computed(() => {
  const currentStepName = stepsStore.getCurrentStep;
  const lastIndex = steps.value.length - 1;
  const lastStepName = steps.value[lastIndex].name;
  return currentStepName === lastStepName; 
});
const toStepButtonsList = computed(() => configStore.getToStepButtonsList);
const hasVideo = computed(() => !!configStore.getVideoData);

function handleCustomNext(lineItem: ILineItem | null) {
  emit(E_STEPS_CONTROLLER_EVENT.CUSTOM_NEXT, lineItem);
}
</script>

<style scoped lang="scss">
.steps-controller {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px 0;
  gap: 3px;
  flex-wrap: wrap;

  &__button {
    display: flex;
    gap: 8px;
  }

  &__left-buttons {
    gap: calc(16px*2);
  }
  &__right-buttons {
    max-width: 564px;
    width: 100%;

    &_4 {
      max-width: 648px;
      display: grid;
      gap: 8px;
      grid-template-columns: 1.5fr 1fr 1fr 1fr;

      @media screen and (max-width: 663px) {
        grid-template-columns: 1fr 1fr;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .steps-controller {
    justify-content: flex-end;
    padding: 8px 0;

    &__left-buttons {
      display: none;
    }
  }
}
</style>